<template>
  <v-container class="pa-4 pa-sm-8" fluid>
    <v-row class="mb-4">
      <v-col cols="12">
        <h2 class="text-h5 font-weight-bold">Portioning</h2>
      </v-col>
    </v-row>

    <v-card class="pa-8" elevation="0" style="background-color: #f5f5f5;">
      <v-row align="center" justify="space-between">
        <v-col cols="auto">
          <span class="text-subtitle1 font-weight-medium">Enable Portioning</span>
        </v-col>
        <v-col cols="auto">
          <v-switch
            v-model="isPortionEnabled"
            color="primary"
            hide-details
            inset
            @change="updatePortionEnabled"
            :label="isPortionEnabled ? 'ON' : 'OFF'"
          ></v-switch>
        </v-col>
      </v-row>
    </v-card>

    <v-row class="mt-6">
      <v-col cols="12">
        <v-btn color="primary" elevation="0" @click="openAddDialog" class="px-6 text-capitalize">
          + Add Portion
        </v-btn>
      </v-col>
    </v-row>

    <v-card class="mt-4 elevation-0">
      <v-data-table :headers="headers" :items="portionsList" :items-per-page="10" class="elevation-1" :loading="loading"
        hide-default-footer>
        <template v-slot:item.name="{ item }">
          <span class="font-weight-medium">{{ item.name }}</span>
        </template>
        <template v-slot:item.value="{ item }">
          {{ item.value }}
        </template>
        <template v-slot:item.calorieRange="{ item }">
          {{ item.calorieRange || '-' }}
        </template>
        <template v-slot:item.isActive="{ item }">
          <v-chip :color="item.isActive ? 'primary' : 'grey'" small dark class="font-weight-medium">
            {{ item.isActive ? 'Enabled' : 'Disabled' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center action-wrapper">
            <v-btn text small color="primary" class="mr-2 font-weight-medium" @click="editPortion(item)">
              Edit
            </v-btn>

            <v-switch v-model="item.isActive" color="primary" hide-details inset class="ma-0 pa-0"
              @change="toggleStatus(item)"></v-switch>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          {{ editingPortion ? 'Edit Portion' : 'Add Portion' }}
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" label="Portion Name" outlined class="mt-2"></v-text-field>
          <v-text-field v-model.number="form.value" label="Portion Value" type="number" step="0.25"
            outlined></v-text-field>
          <v-text-field v-model="form.calorieRange" label="Calorie Range" placeholder="e.g., 100-200"
            outlined :rules="[calorieRangeValidator]" hint="Format: min-max (e.g. 100-200)" persistent-hint></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="savePortion">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      isPortionEnabled: false,
      portionsList: [],
      loading: false,
      headers: [
        { text: 'Portion Name', value: 'name', sortable: false },
        { text: 'Portion Value', value: 'value', sortable: false },
        { text: 'Calorie Range', value: 'calorieRange', sortable: false },
        { text: 'Status', value: 'isActive', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      showDialog: false,
      editingPortion: null,
      form: {
        name: '',
        value: null,
        calorieRange: '',
        isActive: true
      },
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },

  apollo: {
    getAppConfigurations: {
      query: require("../api/getAppConfigurations.graphql"),
      result({ data }) {
        if (data && data.getAppConfigurations) {
          this.isPortionEnabled = data.getAppConfigurations.isPortionEnabled || false;
        }
      },
    },
    getPortions: {
      query: require("../api/getPortions.graphql"),
      loadingKey: 'loading',
      result({ data }) {
        this.portionsList = data.getPortions || [];
      },
    },
  },

  methods: {
    openAddDialog() {
      this.editingPortion = null;
      this.form = { name: '', value: null, calorieRange: '', isActive: true };
      this.showDialog = true;
    },

    editPortion(item) {
      this.editingPortion = item;
      this.form = {
        name: item.name,
        value: item.value,
        calorieRange: item.calorieRange || '',
        isActive: item.isActive
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingPortion = null;
      this.form = { name: '', value: null, calorieRange: '', isActive: true };
    },

    async savePortion() {
      if (!this.form.name || !this.form.value) {
        this.showSnackbar('Please fill all fields', 'error');
        return;
      }

      if (this.form.calorieRange && this.calorieRangeValidator(this.form.calorieRange) !== true) {
        this.showSnackbar('Calorie Range format must be min-max (e.g. 100-200)', 'error');
        return;
      }

      const editingId = this.editingPortion ? this.editingPortion._id : null;
      const duplicateName = this.portionsList.find(
        p => p.name.trim().toLowerCase() === this.form.name.trim().toLowerCase() && p._id !== editingId
      );
      if (duplicateName) {
        this.showSnackbar('A portion with this name already exists', 'error');
        return;
      }
      const duplicateValue = this.portionsList.find(
        p => parseFloat(p.value) === parseFloat(this.form.value) && p._id !== editingId
      );
      if (duplicateValue) {
        this.showSnackbar(`Portion value ${this.form.value} already exists (${duplicateValue.name})`, 'error');
        return;
      }

      try {
        const input = {
          name: this.form.name,
          value: parseFloat(this.form.value),
          calorieRange: this.form.calorieRange,
          isActive: this.form.isActive,
        };

        if (this.editingPortion) {
          input._id = this.editingPortion._id;
        }

        await this.$apollo.mutate({
          mutation: require("../api/upsertPortion.graphql"),
          variables: { input },
        });

        this.showSnackbar(
          this.editingPortion ? 'Portion updated successfully' : 'Portion added successfully',
          'success'
        );

        this.$apollo.queries.getPortions.refetch();
        this.$store.dispatch('fetchPortionConfig');
        this.closeDialog();
      } catch (error) {
        console.error('Error saving portion:', error);
        this.showSnackbar('Error saving portion', 'error');
      }
    },

    async toggleStatus(item) {
      const isDisabling = !item.isActive; // v-model already flipped the value
      if (isDisabling && this.isPortionEnabled) {
        const remainingActive = this.portionsList.filter(p => p.isActive).length;
        if (remainingActive === 0) {
          item.isActive = true; // revert the v-switch flip
          this.showSnackbar('Cannot disable the last active portion while portioning is enabled.', 'error');
          return;
        }
      }

      try {
        await this.$apollo.mutate({
          mutation: require("../api/togglePortionStatus.graphql"),
          variables: { _id: item._id },
        });
        this.showSnackbar('Status updated successfully', 'success');
        this.$apollo.queries.getPortions.refetch();
        this.$store.dispatch('fetchPortionConfig');
      } catch (error) {
        console.error('Error toggling status:', error);
        item.isActive = !item.isActive;
        this.showSnackbar('Error updating status', 'error');
      }
    },

    async updatePortionEnabled() {
      const newState = this.isPortionEnabled;
      try {
        await this.$apollo.mutate({
          mutation: require("../api/updateConfigurations.graphql"),
          variables: { isPortionEnabled: newState },
        });
        this.showSnackbar(`Portioning ${newState ? 'enabled' : 'disabled'} successfully`, 'success');
        this.$store.dispatch('fetchPortionConfig');
      } catch (error) {
        console.error('Error updating portion feature:', error);
        this.isPortionEnabled = !newState; // revert on error
        this.showSnackbar('Error updating portion feature', 'error');
      }
    },

    calorieRangeValidator(v) {
      if (!v) return true;
      return /^\d+(\.\d+)?-\d+(\.\d+)?$/.test(v.trim()) || 'Format must be min-max (e.g. 100-200)';
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>
